import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Tooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import { ACTION_BUTTON_SX, ACTION_GRID_SX } from "./cardConstants";
import { eventCardShape } from "./eventPropTypes";

const TOOLTIP_BUTTON_SPAN_STYLE = { width: "100%", display: "block" };
const PRIMARY_MANAGE_ACTION_SX = {
  ...ACTION_BUTTON_SX,
  minHeight: 40,
  fontWeight: 700,
  whiteSpace: "normal",
  wordBreak: "keep-all",
};
const SECONDARY_OUTLINED_ACTION_SX = {
  ...ACTION_BUTTON_SX,
  borderColor: "divider",
  bgcolor: "background.paper",
  "&:hover": {
    borderColor: "text.disabled",
    bgcolor: "action.hover",
  },
};

function TooltipButton({
  title,
  onClick,
  startIcon,
  variant,
  color,
  sx,
  children,
  disabled,
}) {
  return (
    <Tooltip title={title} placement="top">
      <span style={TOOLTIP_BUTTON_SPAN_STYLE}>
        <Button
          fullWidth
          variant={variant}
          color={color}
          onClick={onClick}
          startIcon={startIcon}
          sx={sx}
          disabled={disabled}
        >
          {children}
        </Button>
      </span>
    </Tooltip>
  );
}

TooltipButton.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  startIcon: PropTypes.node,
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  color: PropTypes.oneOf([
    "inherit",
    "primary",
    "secondary",
    "success",
    "error",
    "info",
    "warning",
  ]),
  sx: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.arrayOf(PropTypes.shape({})),
  ]),
  children: PropTypes.node,
  disabled: PropTypes.bool,
};

TooltipButton.defaultProps = {
  onClick: undefined,
  startIcon: null,
  variant: "outlined",
  color: "primary",
  sx: undefined,
  children: null,
  disabled: false,
};

export default function AdminEventActions({
  event,
  manageConfig,
  includeBookingActions,
  isPublicBookingEnabled,
  onToggleVisibility,
  onManageParticipants,
  onEdit,
  onCollectFees,
  onDelete,
  onOpenPublicBooking,
  onCopyPublicBookingLink,
  onShowPublicBookingQR,
  publicPageTooltip,
  copyLinkTooltip,
  qrCodeTooltip,
}) {
  return (
    <>
      <TooltipButton
        title={
          event?.isPublic
            ? "Veranstaltung im Mitgliederportal ausblenden – Mitglieder können sie dann nicht mehr sehen"
            : "Veranstaltung für Mitglieder sichtbar schalten – sie erscheint dann im Mitgliederportal"
        }
        color={event?.isPublic ? "warning" : "success"}
        variant="contained"
        onClick={onToggleVisibility}
        startIcon={
          <Icon icon={event?.isPublic ? "mdi:eye-off-outline" : "mdi:eye-outline"} />
        }
        sx={{
          ...PRIMARY_MANAGE_ACTION_SX,
          letterSpacing: "0.01em",
        }}
      >
        {event?.isPublic ? "Für Mitglieder verbergen" : "Für Mitglieder veröffentlichen"}
      </TooltipButton>

      {includeBookingActions ? (
        <Box sx={ACTION_GRID_SX}>
          <TooltipButton
            title={publicPageTooltip}
            disabled={!isPublicBookingEnabled}
            variant="outlined"
            onClick={onOpenPublicBooking}
            startIcon={<Icon icon="mdi:open-in-new" />}
            sx={SECONDARY_OUTLINED_ACTION_SX}
          >
            Buchungsseite
          </TooltipButton>

          <TooltipButton
            title={copyLinkTooltip}
            disabled={!isPublicBookingEnabled}
            variant="outlined"
            onClick={onCopyPublicBookingLink}
            startIcon={<Icon icon="mdi:content-copy" />}
            sx={SECONDARY_OUTLINED_ACTION_SX}
          >
            Buchungslink
          </TooltipButton>

          <TooltipButton
            title={qrCodeTooltip}
            disabled={!isPublicBookingEnabled}
            variant="outlined"
            onClick={onShowPublicBookingQR}
            startIcon={<Icon icon="mdi:qrcode" />}
            sx={SECONDARY_OUTLINED_ACTION_SX}
          >
            QR Code
          </TooltipButton>
        </Box>
      ) : null}

      <Box sx={ACTION_GRID_SX}>
        <TooltipButton
          title={manageConfig.tooltip}
          variant="contained"
          color="primary"
          onClick={onManageParticipants}
          startIcon={<Icon icon={manageConfig.icon} />}
          sx={PRIMARY_MANAGE_ACTION_SX}
        >
          {manageConfig.label}
        </TooltipButton>

        <TooltipButton
          title="Veranstaltungsdetails (Titel, Datum, Ort, Typ etc.) bearbeiten"
          variant="outlined"
          onClick={onEdit}
          startIcon={<Icon icon="mdi:pencil" />}
          sx={SECONDARY_OUTLINED_ACTION_SX}
        >
          Bearbeiten
        </TooltipButton>
      </Box>

      <Box
        sx={{
          ...ACTION_GRID_SX,
          gridTemplateColumns: event?.isFeeBased
            ? ACTION_GRID_SX.gridTemplateColumns
            : "1fr",
        }}
      >
        {event?.isFeeBased ? (
          <TooltipButton
            title={`Teilnahmegebühr von ${Number(event?.feeAmount || 0).toFixed(2)} EUR per SEPA-Lastschrift von angemeldeten Mitgliedern einziehen`}
            variant="outlined"
            color="warning"
            onClick={onCollectFees}
            startIcon={<Icon icon="mdi:cash-multiple" />}
            sx={{
              ...SECONDARY_OUTLINED_ACTION_SX,
              borderColor: "warning.main",
              bgcolor: "rgba(245, 158, 11, 0.06)",
            }}
          >
            SEPA einziehen
          </TooltipButton>
        ) : null}

        <TooltipButton
          title="Veranstaltung dauerhaft löschen – diese Aktion kann nicht rückgängig gemacht werden"
          variant="outlined"
          color="error"
          onClick={onDelete}
          startIcon={<Icon icon="mdi:trash-can-outline" />}
          sx={{
            ...SECONDARY_OUTLINED_ACTION_SX,
            borderColor: "error.main",
            bgcolor: "rgba(239, 68, 68, 0.05)",
          }}
        >
          Löschen
        </TooltipButton>
      </Box>
    </>
  );
}

AdminEventActions.propTypes = {
  event: eventCardShape,
  manageConfig: PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.string,
    tooltip: PropTypes.string,
  }),
  includeBookingActions: PropTypes.bool,
  isPublicBookingEnabled: PropTypes.bool,
  onToggleVisibility: PropTypes.func,
  onManageParticipants: PropTypes.func,
  onEdit: PropTypes.func,
  onCollectFees: PropTypes.func,
  onDelete: PropTypes.func,
  onOpenPublicBooking: PropTypes.func,
  onCopyPublicBookingLink: PropTypes.func,
  onShowPublicBookingQR: PropTypes.func,
  publicPageTooltip: PropTypes.string,
  copyLinkTooltip: PropTypes.string,
  qrCodeTooltip: PropTypes.string,
};

AdminEventActions.defaultProps = {
  event: null,
  manageConfig: {
    label: "Teilnehmer",
    icon: "mdi:account-multiple-plus",
    tooltip: "Angemeldete Teilnehmer einsehen, hinzufügen oder entfernen",
  },
  includeBookingActions: false,
  isPublicBookingEnabled: false,
  onToggleVisibility: undefined,
  onManageParticipants: undefined,
  onEdit: undefined,
  onCollectFees: undefined,
  onDelete: undefined,
  onOpenPublicBooking: undefined,
  onCopyPublicBookingLink: undefined,
  onShowPublicBookingQR: undefined,
  publicPageTooltip: "",
  copyLinkTooltip: "",
  qrCodeTooltip: "",
};
